import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLangPath, type Language, type RouteKey } from '../../router/langRoutes';
import { useCurrentRouteKey } from '../../hooks/useCurrentRouteKey';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopInvestmentOpen, setIsDesktopInvestmentOpen] = useState(false);
  const [isMobileInvestmentOpen, setIsMobileInvestmentOpen] = useState(false);
  const [isDesktopLanguageOpen, setIsDesktopLanguageOpen] = useState(false);
  const [isMobileLanguageOpen, setIsMobileLanguageOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const currentRouteKey = useCurrentRouteKey();

  const investmentRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Đóng mobile menu khi chuyển trang
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileInvestmentOpen(false);
    setIsDesktopInvestmentOpen(false);
    setIsDesktopLanguageOpen(false);
    setIsMobileLanguageOpen(false);
  }, [location.pathname]);

  // Đóng dropdown desktop khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (investmentRef.current && !investmentRef.current.contains(e.target as Node)) {
        setIsDesktopInvestmentOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(e.target as Node)) {
        setIsDesktopLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lang: string) => {
    const newLang = lang as Language;
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    setIsDesktopLanguageOpen(false);
    setIsMobileLanguageOpen(false);
    
    // Redirect sang path tương đương trong ngôn ngữ mới
    if (currentRouteKey) {
      const newPath = getLangPath(currentRouteKey, newLang);
      navigate(newPath);
    } else {
      // Nếu không xác định được routeKey, redirect về trang chủ
      navigate(getLangPath('home', newLang));
    }
  };

  const getLanguageLabel = () => {
    switch (i18n.language) {
      case 'vi': return 'Tiếng Việt';
      case 'en': return 'English';
      case 'lv': return 'Latviešu';
      default: return 'Tiếng Việt';
    }
  };

  const getLanguageCode = () => {
    switch (i18n.language) {
      case 'vi': return 'VI';
      case 'en': return 'EN';
      case 'lv': return 'LV';
      default: return 'VI';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileInvestmentOpen(false);
    setIsMobileLanguageOpen(false);
    setIsDesktopLanguageOpen(false);
  };

  const currentLang = i18n.language as Language;
  const isHomePage = currentRouteKey === 'home' || currentRouteKey === 'exportServices';
  const isLatvian = i18n.language === 'lv';

  // Kiểm tra active cho từng link
  const isActive = (routeKey: RouteKey) => currentRouteKey === routeKey;
  const isInvestmentActive = currentRouteKey === 'latvia' || 
                             currentRouteKey === 'latviaDetail' || 
                             currentRouteKey === 'latviaPartnership' || 
                             currentRouteKey === 'latviaBlog';

  const navLinkClass = (routeKey: RouteKey) => {
    const active = isActive(routeKey);
    const base = 'font-medium transition-colors hover:text-teal-600';
    if (active) return `${base} text-teal-600 underline underline-offset-4`;
    return `${base} ${isScrolled || !isHomePage ? 'text-gray-700' : 'text-white'}`;
  };

  const mobileLinkClass = (routeKey: RouteKey) => {
    const active = isActive(routeKey);
    if (active) return 'block px-4 py-3 bg-teal-50 text-teal-600 rounded-lg font-semibold border-l-4 border-teal-500';
    return 'block px-4 py-3 text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors font-medium';
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to={getLangPath('home', currentLang)} className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/659ec9572fd869514b19000180e70d49.png"
              alt="The Daeg International"
              className="h-12 w-auto object-contain"
              style={{ filter: isScrolled || !isHomePage ? 'drop-shadow(0 0 6px rgba(0,0,0,0.15))' : 'drop-shadow(0 0 10px rgba(255,255,255,0.8)) drop-shadow(0 0 20px rgba(255,255,255,0.5))' }}
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">

            {/* Investment Dropdown - Desktop */}
            {!isLatvian && (
              <div className="relative" ref={investmentRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsDesktopInvestmentOpen(!isDesktopInvestmentOpen);
                    setIsDesktopLanguageOpen(false);
                    setIsMobileLanguageOpen(false);
                  }}
                  className={`font-medium transition-colors hover:text-teal-600 flex items-center space-x-1 cursor-pointer ${
                    isInvestmentActive ? 'text-teal-600 underline underline-offset-4' :
                    isScrolled || !isHomePage ? 'text-gray-700' : 'text-white'
                  }`}
                >
                  <span>{t('nav.investment')}</span>
                  <i className={`ri-arrow-down-s-line text-lg ${isDesktopInvestmentOpen ? 'rotate-180' : ''} transition-transform`}></i>
                </button>

                {isDesktopInvestmentOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                    <Link
                      to={getLangPath('latvia', currentLang)}
                      onClick={() => setIsDesktopInvestmentOpen(false)}
                      className={`block px-4 py-3 transition-colors font-medium ${isActive('latvia') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'}`}
                    >
                      {t('nav.investment.main')}
                    </Link>
                    <Link
                      to={getLangPath('latviaDetail', currentLang)}
                      onClick={() => setIsDesktopInvestmentOpen(false)}
                      className={`block px-4 py-3 transition-colors font-medium ${isActive('latviaDetail') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'}`}
                    >
                      {t('nav.investment.detail')}
                    </Link>
                    <Link
                      to={getLangPath('latviaPartnership', currentLang)}
                      onClick={() => setIsDesktopInvestmentOpen(false)}
                      className={`block px-4 py-3 transition-colors font-medium ${isActive('latviaPartnership') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'}`}
                    >
                      {t('nav.investment.partnership')}
                    </Link>
                    <Link
                      to={getLangPath('latviaBlog', currentLang)}
                      onClick={() => setIsDesktopInvestmentOpen(false)}
                      className={`block px-4 py-3 transition-colors font-medium ${isActive('latviaBlog') ? 'bg-teal-50 text-teal-600' : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'}`}
                    >
                      {t('nav.investment.blog')}
                    </Link>
                  </div>
                )}
              </div>
            )}

            <Link to={getLangPath('choHem', currentLang)} className={navLinkClass('choHem')}>{t('nav.choHem')}</Link>
            <Link to={getLangPath('exportServices', currentLang)} className={navLinkClass('exportServices')}>{t('nav.home')}</Link>
            <Link to={getLangPath('about', currentLang)} className={navLinkClass('about')}>{t('nav.about')}</Link>
            <Link to={getLangPath('contact', currentLang)} className={navLinkClass('contact')}>{t('nav.contact')}</Link>

            {/* Language Dropdown - Desktop */}
            <div className="relative" ref={languageRef}>
              <button
                type="button"
                onClick={() => {
                  setIsDesktopLanguageOpen(!isDesktopLanguageOpen);
                  setIsDesktopInvestmentOpen(false);
                }}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all hover:bg-teal-50 cursor-pointer ${
                  isScrolled || !isHomePage ? 'text-gray-700' : 'text-white hover:text-teal-600'
                }`}
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-semibold">{getLanguageCode()}</span>
                <i className={`ri-arrow-down-s-line text-sm ${isDesktopLanguageOpen ? 'rotate-180' : ''} transition-transform`}></i>
              </button>

              {isDesktopLanguageOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                  {(['vi', 'en', 'lv'] as const).map((lang) => {
                    const flags: Record<string, string> = { vi: '🇻🇳', en: '🇬🇧', lv: '🇱🇻' };
                    const labels: Record<string, string> = { vi: 'Tiếng Việt', en: 'English', lv: 'Latviešu' };
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => changeLanguage(lang)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-teal-50 hover:text-teal-600 transition-colors flex items-center space-x-2 cursor-pointer ${
                          i18n.language === lang ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-base">{flags[lang]}</span>
                        <span>{labels[lang]}</span>
                        {i18n.language === lang && <i className="ri-check-line ml-auto text-teal-600"></i>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Customer Portal Button */}
            <a
              href="/customer/login"
              className="flex items-center gap-1.5 border border-[#8B6914] text-[#8B6914] px-4 py-2 rounded-lg font-medium hover:bg-[#8B6914] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-user-line text-sm"></i>
              Khách Hàng
            </a>

            <button
              type="button"
              onClick={() => {
                const consultButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                if (consultButton) consultButton.click();
              }}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.consult')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              isScrolled || !isHomePage ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">

            {/* Dịch Vụ Định Cư - Mobile (ẩn khi đang dùng tiếng Latvia) */}
            {!isLatvian && (
              <div>
                <button
                  type="button"
                  onClick={() => setIsMobileInvestmentOpen(!isMobileInvestmentOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors font-medium cursor-pointer ${
                    isInvestmentActive
                      ? 'bg-teal-50 text-teal-600 border-l-4 border-teal-500'
                      : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-building-line text-base"></i>
                    </div>
                    <span>{t('nav.investment')}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileInvestmentOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isMobileInvestmentOpen && (
                  <div className="mt-1 ml-4 space-y-1 border-l-2 border-teal-100 pl-3">
                    <Link
                      to={getLangPath('latvia', currentLang)}
                      onClick={closeMobileMenu}
                      className={`block px-3 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                        isActive('latvia') ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {isActive('latvia') && <i className="ri-checkbox-blank-circle-fill text-teal-500 text-xs"></i>}
                        <span>{t('nav.investment.main')}</span>
                      </div>
                    </Link>
                    <Link
                      to={getLangPath('latviaDetail', currentLang)}
                      onClick={closeMobileMenu}
                      className={`block px-3 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                        isActive('latviaDetail') ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {isActive('latviaDetail') && <i className="ri-checkbox-blank-circle-fill text-teal-500 text-xs"></i>}
                        <span>{t('nav.investment.detail')}</span>
                      </div>
                    </Link>
                    <Link
                      to={getLangPath('latviaPartnership', currentLang)}
                      onClick={closeMobileMenu}
                      className={`block px-3 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                        isActive('latviaPartnership') ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {isActive('latviaPartnership') && <i className="ri-checkbox-blank-circle-fill text-teal-500 text-xs"></i>}
                        <span>{t('nav.investment.partnership')}</span>
                      </div>
                    </Link>
                    <Link
                      to={getLangPath('latviaBlog', currentLang)}
                      onClick={closeMobileMenu}
                      className={`block px-3 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                        isActive('latviaBlog') ? 'bg-teal-50 text-teal-600 font-semibold' : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {isActive('latviaBlog') && <i className="ri-checkbox-blank-circle-fill text-teal-500 text-xs"></i>}
                        <span>{t('nav.investment.blog')}</span>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Chợ Hẻm */}
            <Link to={getLangPath('choHem', currentLang)} onClick={closeMobileMenu} className={mobileLinkClass('choHem')}>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-store-2-line text-base"></i>
                </div>
                <span>{t('nav.choHem')}</span>
              </div>
            </Link>

            {/* Dịch Vụ Xuất Khẩu */}
            <Link to={getLangPath('exportServices', currentLang)} onClick={closeMobileMenu} className={mobileLinkClass('exportServices')}>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-ship-line text-base"></i>
                </div>
                <span>{t('nav.home')}</span>
              </div>
            </Link>

            {/* Về Chúng Tôi */}
            <Link to={getLangPath('about', currentLang)} onClick={closeMobileMenu} className={mobileLinkClass('about')}>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-team-line text-base"></i>
                </div>
                <span>{t('nav.about')}</span>
              </div>
            </Link>

            {/* Liên Hệ */}
            <Link to={getLangPath('contact', currentLang)} onClick={closeMobileMenu} className={mobileLinkClass('contact')}>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-base"></i>
                </div>
                <span>{t('nav.contact')}</span>
              </div>
            </Link>

            {/* Divider */}
            <div className="border-t border-gray-100 my-2 pt-2">

              {/* Language Switcher Mobile — chỉ đóng dropdown, KHÔNG đóng toàn bộ menu */}
              <button
                type="button"
                onClick={() => setIsMobileLanguageOpen(!isMobileLanguageOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors font-medium cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-global-line text-base"></i>
                  </div>
                  <span>{getLanguageLabel()}</span>
                </div>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileLanguageOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileLanguageOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-teal-100 pl-3">
                  {(['vi', 'en', 'lv'] as const).map((lang) => {
                    const flags: Record<string, string> = { vi: '🇻🇳', en: '🇬🇧', lv: '🇱🇻' };
                    const labels: Record<string, string> = { vi: 'Tiếng Việt', en: 'English', lv: 'Latviešu' };
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => changeLanguage(lang)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer text-sm font-medium ${
                          i18n.language === lang
                            ? 'bg-teal-50 text-teal-600 font-semibold'
                            : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                        }`}
                      >
                        <span className="text-base">{flags[lang]}</span>
                        <span>{labels[lang]}</span>
                        {i18n.language === lang && <i className="ri-check-line ml-auto text-teal-600"></i>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Nút Khách Hàng */}
            <a
              href="/customer/login"
              onClick={closeMobileMenu}
              className="w-full border border-[#8B6914] text-[#8B6914] px-6 py-3 rounded-lg font-medium hover:bg-[#8B6914] hover:text-white transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line text-base"></i>
              </div>
              <span>Cổng Thông Tin Khách Hàng</span>
            </a>

            {/* Nút Tư Vấn */}
            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                setTimeout(() => {
                  const consultButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (consultButton) consultButton.click();
                }, 300);
              }}
              className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-base"></i>
              </div>
              <span>{t('nav.consult')}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}